import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";

import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { StyledProps } from "@ppb/the-wall-common/types";
import type { URN } from "../../types";
import {
  BetslipCloseAction,
  BetslipExchangeConfirmBetsClickAction,
  UI__BETSLIP_CLOSE_CLICK,
  UI__BETSLIP_EXC_CONFIRM_BET_CLICK,
} from "@ppb/tbd-store/actions/betslip";
import {
  getBetslipExchangeContext,
  getBetslipExchangePlaceError,
  getIsFreeBetsSelected,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { getExchangeRunnerTree } from "../../store/state/exchange-markets/exchange-markets-selectors";
import { createRunnerPotentialBetsSelector } from "../../store/state/exchange-betting/runner-potential-bets-selectors";
import {
  getExchangeEligibleBonusByMarketId,
  getRunnerPotentialBetWithBonus,
} from "../../store/state/exchange-betting-bonus/exchange-betting-bonus-selectors";
import { getExchangePotentialState } from "../../store/state/exchange-betting-potential/exchange-betting-potential-selectors";
import { getRunnerSPBet } from "../../store/state/exchange-betting/exchange-betting-selectors";
import { BETTING__DEPOSIT_TO_PLACE_BET, BettingDepositToPlaceBetAction } from "@ppb/tbd-store/actions/betting";
import {
  BETTING__REMOVE_ALL_POTENTIAL_BETS_ACTION,
  type RemoveAllPotentialBetsAction,
} from "../../store/state/exchange-betting-potential/exchange-betting-potential-actions";
import { emitBetslipDepositToPlaceRequested } from "../../contract/betslip-api";

import { i18n } from "../../helpers/i18n";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { getInlineBetslipTitle, isEachWayMarket, isLineBettingType } from "../../helpers/exchange-betslip-helper";
import {
  buildExchangeTransactionalError,
  buildFreeBetsLabel,
  computeEachWayExposure,
  getHandicapOutcomeRows,
  ConfirmBet,
  getBetData,
  getEachWayBetData,
  getPotentialExposure,
} from "../betslip-formatters";
import {
  getHandicapOutcomes,
  getHandicapPnlTable,
  getHomeSelectionId,
  getOutcomeRunners,
} from "../../helpers/handicap-outcomes";
import type { OutcomeBetSegmentsProps } from "../OutcomeBetSegments/OutcomeBetSegments.types";
import type { ExchangeInlineConfirmPanelProps } from "./snowflakes/ExchangeInlineConfirmPanel/ExchangeInlineConfirmPanel.types";
import { isBPEAvailable } from "../../helpers/best-price-execution";
import type {
  BetslipLocalization,
  BetslipSession,
  ExchangeBetslipCapabilities,
  ExchangeBetslipPreferences,
} from "../../contract";
import {
  BetslipExchangeConfirmPanelBackClickAction,
  UI__BETSLIP_EXC_CONFIRM_PANEL_BACK_CLICK,
} from "../../store/state/betslip-module/betslip-module-exchange-actions";

export type ContainerProps = {
  localization: BetslipLocalization;
  session: BetslipSession;
  capabilities: ExchangeBetslipCapabilities;
  preferences: ExchangeBetslipPreferences;
};

type CardProps = {
  labels: ExchangeInlineConfirmPanelProps["labels"];
  urn?: URN;
  title: ExchangeInlineConfirmPanelProps["title"];
  titlePrefix?: ExchangeInlineConfirmPanelProps["titlePrefix"];
  freeBets?: ExchangeInlineConfirmPanelProps["freeBets"];
  side?: ExchangeInlineConfirmPanelProps["side"];
  price?: ExchangeInlineConfirmPanelProps["price"];
  size?: ExchangeInlineConfirmPanelProps["size"];
  currencySymbol: ExchangeInlineConfirmPanelProps["currencySymbol"];
  profitLabel: ExchangeInlineConfirmPanelProps["profitLabel"];
  profitValue: ExchangeInlineConfirmPanelProps["profitValue"];
  profitRawValue: ExchangeInlineConfirmPanelProps["profitRawValue"];
  confirm: StyledProps["translation"];
  loading: ExchangeInlineConfirmPanelProps["loading"];
  error?: ExchangeInlineConfirmPanelProps["error"];
  betDelay?: ExchangeInlineConfirmPanelProps["betDelay"];
  isDepositRequired: boolean;
  isFreeBetsSelected: ExchangeInlineConfirmPanelProps["isFreeBetsSelected"];
  matchMePrefix?: string;
  multiplier?: ExchangeInlineConfirmPanelProps["multiplier"];
  /** The labelled-row strip: the each-way trio, or one row per outcome on a handicap or totals bet. */
  outcomeSegments?: OutcomeBetSegmentsProps;
  isSpActive: ExchangeInlineConfirmPanelProps["isSpActive"];
  isOddsLimitActive: ExchangeInlineConfirmPanelProps["isOddsLimitActive"];
  isLineBettingType?: ExchangeInlineConfirmPanelProps["isLineBettingType"];
};

export type StateProps = CardProps | Record<string, never>;

function resolveSpProfitDisplay({
  isSpWithOddsLimit,
  isSpNoLimitBack,
  isSpNoLimitLay,
  size,
  minProfit,
  betData,
  localization,
}: {
  isSpWithOddsLimit: boolean;
  isSpNoLimitBack: boolean;
  isSpNoLimitLay: boolean;
  size: number | undefined;
  minProfit: number | undefined;
  betData: { label: string; value: string };
  localization: BetslipLocalization;
}) {
  let profitLabel = betData.label;
  let profitValue = betData.value;

  if (isSpWithOddsLimit) {
    profitLabel = i18n({ key: "I18N.BSP.MIN_PROFIT" });
    profitValue =
      minProfit !== undefined
        ? currencyFormatWithDecimalPlaces({ ...localization, value: minProfit, decimalPlaces: 2 })
        : betData.value;
  } else if (isSpNoLimitBack) {
    profitLabel = `${i18n({ key: "I18N.BETSLIP.PROFIT" })}:`;
    profitValue = i18n({ key: "I18N.BETSLIP.TBD" });
  } else if (isSpNoLimitLay) {
    profitLabel = `${i18n({ key: "I18N.BETSLIP.LIABILITY" })}:`;
    profitValue = currencyFormatWithDecimalPlaces({ ...localization, value: size ?? 0, decimalPlaces: 2 });
  }

  return { profitLabel, profitValue };
}

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const getRunnerPotentialBets = createRunnerPotentialBetsSelector();
  const labels = {
    edit: i18n({ key: "I18N.BETSLIP.EDIT_BET" }),
    price: i18n({ key: "I18N.BETSLIP.ODDS" }),
    size: i18n({ key: "I18N.BETSLIP.STAKE" }),
    oddsLimitTitle: i18n({ key: "I18N.BSP.SP_ODDS_LIMIT_TITLE" }),
  };
  const noStateProps = {
    labels,
    isFreeBetsSelected: false,
    isDepositRequired: false,
    isSpActive: false,
    isOddsLimitActive: false,
    profitLabel: "",
    profitValue: "",
    profitRawValue: 0,
    confirm: "",
    loading: "",
    title: "",
    currencySymbol: "",
  };

  return (
    appState: BetslipApplicationState,
    { localization, session, capabilities, preferences }: ContainerProps,
  ): StateProps => {
    const { currencySymbol } = localization;

    if (!getIsBetslipLoaded(appState)) {
      return {};
    }

    const betslipContext = getBetslipExchangeContext(appState);

    if (!betslipContext || !currencySymbol) {
      return noStateProps;
    }

    const [potentialBet] = getRunnerPotentialBets(appState, betslipContext.runner);
    const runnerTree = getExchangeRunnerTree(appState, betslipContext.runner);

    if (!potentialBet || !runnerTree) {
      return noStateProps;
    }

    const exchangePlaceError = getBetslipExchangePlaceError(appState);
    const { runner, market, marketRunner } = runnerTree;
    const { betDelay, marketId, bettingType, marketType } = market;
    const { size, price, side } = potentialBet;
    const multiplier = isEachWayMarket(marketType) ? i18n({ key: "I18N.BETSLIP.2X_MULTIPLIER" }) : undefined;
    const { isSp, isOddsLimitActive } = getExchangePotentialState(appState, runner.urn, side) ?? {};
    const isSpActive = !!isSp && (price === undefined || (isOddsLimitActive ?? false));

    const runnerSPBet = getRunnerSPBet(appState, market.urn, marketRunner, side);
    const minProfit = runnerSPBet?.minProfit;

    const isFreeBetsSelected = getIsFreeBetsSelected(appState);
    const isDepositRequired = exchangePlaceError?.errorCode === "INSUFFICIENT_FUNDS";

    const potentialBetData = isFreeBetsSelected
      ? getRunnerPotentialBetWithBonus(appState, runner, marketId, marketType, bettingType)
      : potentialBet;
    const eachWayExposure = computeEachWayExposure(
      {
        side,
        selectionId: runner.selectionId,
        price: potentialBetData?.price ?? price,
        size: potentialBetData?.size ?? size,
      },
      { marketType, bettingType, eachWayDivisor: market.eachWayDivisor },
    );
    const betData = getBetData(
      { ...potentialBetData, side },
      localization,
      isDepositRequired,
      capabilities.eachWayExposureDisplay.enabled ? eachWayExposure : undefined,
    );
    // A Handicap or Totals bet resolves to a different profit per goal margin, so it fills the same
    // labelled-row strip as an each-way bet, one row per outcome instead of win / placed / unplaced.
    // The engine's table is empty until it has priced a stake, which keeps the rows hidden until then.
    const pnlTable = getHandicapPnlTable(potentialBetData ?? potentialBet, { marketType, bettingType });
    const outcomeRunners = market.runners && getOutcomeRunners(market.runners, getHomeSelectionId(market));
    const handicapSegments =
      pnlTable && outcomeRunners
        ? {
            rows: getHandicapOutcomeRows(
              getHandicapOutcomes(
                { marketType, selectionId: runner.selectionId, handicap: marketRunner.handicap },
                pnlTable,
                outcomeRunners,
              ),
              localization,
            ),
          }
        : undefined;

    // A market is either each-way or handicap, never both.
    const outcomeSegments = getEachWayBetData(eachWayExposure, localization) ?? handicapSegments;
    const loadingLabel = isDepositRequired ? "" : i18n({ key: "I18N.BETSLIP.PLACING_BET" });

    const priceLabel =
      isSpActive && isOddsLimitActive
        ? i18n({ key: side === "LAY" ? "I18N.BSP.MAX_ODDS" : "I18N.BSP.MIN_ODDS" })
        : labels.price;

    const sizeLabel =
      side === "LAY" ? i18n({ key: isSpActive ? "I18N.BETSLIP.LIABILITY" : "I18N.LABELS.BACKER_STAKE" }) : labels.size;

    const computedLabels = {
      ...labels,
      price: priceLabel,
      size: sizeLabel,
    };

    let freeBetsLabel;

    if (isFreeBetsSelected) {
      const eligibleBonus = getExchangeEligibleBonusByMarketId(
        appState,
        marketId,
        runner.selectionId,
        marketType,
        bettingType,
      );

      if (eligibleBonus > 0) {
        const potentialExposure = getPotentialExposure(potentialBet as ConfirmBet);

        const bonusUsed = potentialExposure > eligibleBonus ? eligibleBonus : potentialExposure;

        freeBetsLabel = buildFreeBetsLabel("I18N.BETSLIP.USING_BONUS", localization, bonusUsed);
      }
    }

    const showMatchMePrefix =
      isBPEAvailable(appState, session, capabilities, preferences) && preferences.matchMe.enabled;
    const isPureSp = isSpActive && !isOddsLimitActive;
    const isSpWithOddsLimit = isSpActive && (isOddsLimitActive ?? false);

    const { profitLabel, profitValue } = resolveSpProfitDisplay({
      isSpWithOddsLimit,
      isSpNoLimitBack: isPureSp && side === "BACK",
      isSpNoLimitLay: isPureSp && side !== "BACK",
      size,
      minProfit,
      betData,
      localization,
    });

    return {
      ...noStateProps,
      labels: computedLabels,
      urn: runner.urn,
      freeBets: freeBetsLabel,
      titlePrefix:
        side === "BACK" ? i18n({ key: "I18N.BETSLIP.BACK_BET_FOR" }) : i18n({ key: "I18N.BETSLIP.LAY_BET_AGAINST" }),
      title: getInlineBetslipTitle(appState),
      side,
      price: isPureSp ? i18n({ key: "I18N.BETSLIP.STARTING_PRICE" }) : price,
      size,
      currencySymbol,
      profitLabel,
      profitValue,
      profitRawValue: betData.rawValue,
      loading: loadingLabel,
      confirm: isDepositRequired
        ? i18n({ key: "I18N.BETSLIP.DEPOSIT_TO_PLACE_BET" })
        : i18n({ key: "I18N.BETSLIP.CONFIRM_BET" }),
      betDelay,
      error: exchangePlaceError ? buildExchangeTransactionalError(exchangePlaceError, currencySymbol) : undefined,
      isDepositRequired,
      isFreeBetsSelected,
      matchMePrefix: showMatchMePrefix ? i18n({ key: "I18N.BETSLIP.MATCH_ME.TITLE" }) : undefined,
      multiplier,
      outcomeSegments,
      isSpActive,
      isOddsLimitActive: isSpActive && (isOddsLimitActive ?? false),
      isLineBettingType: isLineBettingType(bettingType),
    };
  };
};

export type DispatchActions =
  | BetslipCloseAction
  | BetslipExchangeConfirmPanelBackClickAction
  | BetslipExchangeConfirmBetsClickAction
  | BettingDepositToPlaceBetAction
  | RemoveAllPotentialBetsAction;

export type DispatchProps = {
  dispatchConfirmBet: (runner: URN) => void;
  dispatchEdit: () => void;
  dispatchClose: () => void;
  dispatchDepositRedirect: () => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (
  dispatch: Dispatch<DispatchActions>,
) => ({
  dispatchConfirmBet(runner) {
    dispatch<BetslipExchangeConfirmBetsClickAction>({
      type: UI__BETSLIP_EXC_CONFIRM_BET_CLICK,
      payload: {
        runner,
      },
    });
  },
  dispatchEdit() {
    dispatch<BetslipExchangeConfirmPanelBackClickAction>({ type: UI__BETSLIP_EXC_CONFIRM_PANEL_BACK_CLICK });
  },
  dispatchClose() {
    dispatch<RemoveAllPotentialBetsAction>({ type: BETTING__REMOVE_ALL_POTENTIAL_BETS_ACTION });
    dispatch<BetslipCloseAction>({ type: UI__BETSLIP_CLOSE_CLICK });
  },
  dispatchDepositRedirect() {
    dispatch<BettingDepositToPlaceBetAction>({ type: BETTING__DEPOSIT_TO_PLACE_BET });
    emitBetslipDepositToPlaceRequested({});
  },
});
